import { Card, CardContent } from "@/components/ui/card";
import {
  Building2, Globe, Phone, MapPin, Star, Mail, User,
  ExternalLink, CheckCircle2, AlertTriangle, XCircle,
} from "lucide-react";
import { ScoreRing } from "./shared";

export function BusinessOverview({ company, latestAudit, fails, warns, passes }) {
  return (
    <Card data-testid="business-overview-top" className="border-slate-200 shadow-sm">
      <CardContent className="p-5">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Business info */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-slate-500" />
              <h2 className="text-sm font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Business Overview</h2>
            </div>
            <p className="text-[12px] text-slate-600 leading-relaxed mb-2.5">{company.business_summary || "Run Quick Audit or Deep Analyze to generate a business overview."}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground mb-3">
              {company.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{company.phone}</span>}
              {company.domain && <a href={company.website_url || (company.domain.startsWith("http") ? company.domain : `https://${company.domain}`)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-indigo-600"><Globe className="w-3 h-3" />{company.domain}</a>}
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{company.city}, {company.state}</span>
            </div>
            {Array.isArray(company.contacts) && company.contacts.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2.5">
                {company.contacts.filter((ct) => ct && typeof ct === "object").slice(0, 3).map((ct, i) => (
                  <div key={i} data-testid={`overview-contact-${i}`} className="flex items-center gap-1.5 text-[10px] bg-slate-50 border border-slate-100 rounded px-2 py-1">
                    <User className="w-3 h-3 text-slate-400" />
                    <span className="font-medium">{ct.name || "Contact"}</span>
                    {ct.title && <span className="text-muted-foreground">· {ct.title}</span>}
                    {ct.email && <a href={`mailto:${ct.email}`} className="text-indigo-600"><Mail className="w-2.5 h-2.5" /></a>}
                    {ct.phone && <a href={`tel:${ct.phone}`} className="text-indigo-600"><Phone className="w-2.5 h-2.5" /></a>}
                  </div>
                ))}
              </div>
            )}
            {company.social_profiles && Object.keys(company.social_profiles).length > 0 && (
              <div className="flex flex-wrap gap-1">
                {Object.entries(company.social_profiles).map(([p, url]) => (
                  <a key={p} href={url} target="_blank" rel="noopener noreferrer"
                    className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700 capitalize flex items-center gap-0.5">
                    <ExternalLink className="w-2.5 h-2.5" />{p}
                  </a>
                ))}
              </div>
            )}
          </div>
          {/* Scores */}
          <div className="lg:border-l border-slate-100 lg:pl-5">
            <div className="flex items-center justify-between mb-2.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Scores</p>
              {company.google_rating != null && (
                <span className="flex items-center gap-1 text-[11px]"><Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" /><b>{company.google_rating}</b>/5 · {company.google_reviews || 0}</span>
              )}
            </div>
            <div className="space-y-2.5">
              {[
                { label: "Website", score: company.website_score },
                { label: "Presence", score: company.presence_score },
                { label: "Priority", score: company.priority_score },
              ].map((s) => (
                <div key={s.label} className="flex items-center gap-3">
                  <ScoreRing score={s.score} size={48} />
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">{s.label}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {s.score == null ? "Not audited" : s.score >= 70 ? "Good" : s.score >= 40 ? "Needs work" : "Critical"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {latestAudit && (
          <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-100 text-[11px] flex-wrap">
            <span className="flex items-center gap-1 text-rose-600"><XCircle className="w-3 h-3" /> {fails.length} critical</span>
            <span className="flex items-center gap-1 text-amber-600"><AlertTriangle className="w-3 h-3" /> {warns.length} warnings</span>
            <span className="flex items-center gap-1 text-emerald-600"><CheckCircle2 className="w-3 h-3" /> {passes.length} passing</span>
            <span className="ml-auto text-muted-foreground">Audited {latestAudit.created_at ? new Date(latestAudit.created_at).toLocaleDateString() : ""}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
